/**
 * PDF Line Break Estimation for Resume Bullets
 *
 * Port of the Python page_fit.py character-width measurement logic.
 * Uses Liberation Sans / Arial font metrics to predict where line breaks
 * will appear in the generated PDF, without rendering.
 *
 * Key constants from pdf_constants.py:
 *   - chromium_scale: 0.8
 *   - page_width_in: 8.5, body_max_width_in: 8.5
 *   - body_padding_left_in: 0.5, body_padding_right_in: 0.5
 *   - exp_role_indent_em: 0.6 (at 10.5pt base)
 *   - exp_bullet_padding_left_em: 1.4 (at 9.5pt)
 *   - font_size_sm_pt: 9.5 (bullet font size)
 */

// Character widths as fraction of font size (em-units) — Liberation Sans / Arial at 400 weight
const CHAR_WIDTHS: Record<string, number> = {
  ' ': 0.278, '!': 0.278, '"': 0.355, '#': 0.556, '$': 0.556,
  '%': 0.889, '&': 0.667, "'": 0.191, '(': 0.333, ')': 0.333,
  '*': 0.389, '+': 0.584, ',': 0.278, '-': 0.333, '.': 0.278,
  '/': 0.278, '0': 0.556, '1': 0.556, '2': 0.556, '3': 0.556,
  '4': 0.556, '5': 0.556, '6': 0.556, '7': 0.556, '8': 0.556,
  '9': 0.556, ':': 0.278, ';': 0.278, '<': 0.584, '=': 0.584,
  '>': 0.584, '?': 0.556, '@': 1.015,
  'A': 0.667, 'B': 0.667, 'C': 0.722, 'D': 0.722, 'E': 0.667,
  'F': 0.611, 'G': 0.778, 'H': 0.722, 'I': 0.278, 'J': 0.500,
  'K': 0.667, 'L': 0.556, 'M': 0.833, 'N': 0.722, 'O': 0.778,
  'P': 0.667, 'Q': 0.778, 'R': 0.722, 'S': 0.667, 'T': 0.611,
  'U': 0.722, 'V': 0.667, 'W': 0.944, 'X': 0.667, 'Y': 0.667,
  'Z': 0.611,
  '[': 0.278, '\\': 0.278, ']': 0.278, '^': 0.469, '_': 0.556,
  '`': 0.333,
  'a': 0.556, 'b': 0.556, 'c': 0.500, 'd': 0.556, 'e': 0.556,
  'f': 0.278, 'g': 0.556, 'h': 0.556, 'i': 0.222, 'j': 0.222,
  'k': 0.500, 'l': 0.222, 'm': 0.833, 'n': 0.556, 'o': 0.556,
  'p': 0.556, 'q': 0.556, 'r': 0.333, 's': 0.500, 't': 0.278,
  'u': 0.556, 'v': 0.500, 'w': 0.722, 'x': 0.500, 'y': 0.500,
  'z': 0.500,
  '{': 0.334, '|': 0.260, '}': 0.334, '~': 0.584,
};
const DEFAULT_CHAR_WIDTH = 0.556;

// PDF layout constants
const CHROMIUM_SCALE = 0.8;
const PAGE_WIDTH_IN = 8.5;
const BODY_MAX_WIDTH_IN = 8.5;
const BODY_PADDING_LEFT_IN = 0.5;
const BODY_PADDING_RIGHT_IN = 0.5;
const FONT_SIZE_BASE_PT = 10.5;
const FONT_SIZE_SM_PT = 9.5;
const EXP_ROLE_INDENT_EM = 0.6;
const EXP_BULLET_PADDING_LEFT_EM = 1.4;

// Derived: available width for bullet text in points
const effectivePageWidth = PAGE_WIDTH_IN / CHROMIUM_SCALE;
const contentWidthIn = Math.min(effectivePageWidth, BODY_MAX_WIDTH_IN) - BODY_PADDING_LEFT_IN - BODY_PADDING_RIGHT_IN;
const roleIndentIn = (FONT_SIZE_BASE_PT * EXP_ROLE_INDENT_EM) / 72;
const bulletIndentIn = (FONT_SIZE_SM_PT * EXP_BULLET_PADDING_LEFT_EM) / 72;
const BULLET_AVAIL_WIDTH_IN = contentWidthIn - roleIndentIn - bulletIndentIn;
const BULLET_AVAIL_WIDTH_PT = BULLET_AVAIL_WIDTH_IN * 72;

function measureWordWidth(word: string): number {
  let w = 0;
  for (const ch of word) {
    w += CHAR_WIDTHS[ch] ?? DEFAULT_CHAR_WIDTH;
  }
  return w * FONT_SIZE_SM_PT;
}

const SPACE_WIDTH_PT = (CHAR_WIDTHS[' '] ?? 0.278) * FONT_SIZE_SM_PT;

/**
 * Split bullet text into lines as the PDF renderer would.
 * Returns an array of line strings, word-wrapped at the PDF available width.
 */
export function splitBulletIntoLines(text: string): string[] {
  if (!text || !text.trim()) return [''];

  const words = text.split(/\s+/).filter(Boolean);
  if (words.length === 0) return [''];

  const lines: string[] = [];
  let currentLine: string[] = [];
  let currentWidth = 0;

  for (const word of words) {
    const wordWidth = measureWordWidth(word);
    if (currentWidth > 0 && currentWidth + SPACE_WIDTH_PT + wordWidth > BULLET_AVAIL_WIDTH_PT) {
      lines.push(currentLine.join(' '));
      currentLine = [word];
      currentWidth = wordWidth;
    } else {
      if (currentWidth > 0) currentWidth += SPACE_WIDTH_PT;
      currentLine.push(word);
      currentWidth += wordWidth;
    }
  }
  if (currentLine.length > 0) {
    lines.push(currentLine.join(' '));
  }

  return lines;
}

/** Max characters we consider an "orphan" on the last line */
const ORPHAN_MAX_CHARS = 20;

/**
 * Check if the last line of a bullet is an orphan (short dangling text).
 * Returns true if the bullet wraps to 2+ lines and the last line is very short.
 */
export function hasOrphan(lines: string[]): boolean {
  if (lines.length < 2) return false;
  const lastLine = lines[lines.length - 1];
  return lastLine.length <= ORPHAN_MAX_CHARS;
}
